import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiErr, fmt, fmtNum, fileUrl, API_BASE } from "@/lib/api";
import { Button, Input, Label, Card } from "@/components/kit";
import SignaturePad from "@/components/SignaturePad";
import { HardHat, CheckCircle2, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const Row = ({ label, value, strong }) => (
  <div className={`flex items-center justify-between py-2 ${strong ? "border-t-2 border-slate-300 pt-3 mt-1" : "border-b border-slate-100"}`}>
    <span className={strong ? "font-display text-base font-bold text-slate-900" : "text-sm text-slate-600"}>{label}</span>
    <span className={strong ? "font-display text-xl font-bold text-slate-900" : "text-sm font-medium text-slate-800"}>{value}</span>
  </div>
);

export default function PublicSignoff() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/public/signoffs/${token}`).then((r) => setD(r.data)).catch((e) => setErr(apiErr(e, "Länken är ogiltig")));
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const submit = async () => {
    if (!name.trim()) return toast.error("Ange ditt namn");
    if (!sig) return toast.error("Rita eller skriv din signatur");
    setBusy(true);
    try {
      await api.post(`/public/signoffs/${token}/sign`, { contractor_name: name.trim(), signature_type: sig.type, signature_data: sig.data });
      toast.success("Tack! Underlaget är godkänt.");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (err) return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4">
      <Card className="max-w-md p-8 text-center"><p className="text-slate-300">{err}</p></Card>
    </div>
  );
  if (!d) return <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-400">Laddar…</div>;

  const { signoff: s, project: p, company, summary, vat, total, entries } = d;
  const signed = s.status === "signed" || s.status === "invoiced";
  const cur = company.currency || "SEK";
  const showAmt = d.show_amounts;
  const photos = d.include_photos
    ? entries.flatMap((e) => (e.photos || []).map((path) => ({ path, date: e.date, emp: e.employee_name })))
    : [];
  const notes = d.include_notes
    ? entries.filter((e) => (e.notes || "").trim()).map((e) => ({ note: e.notes, date: e.date, emp: e.employee_name }))
    : [];

  return (
    <div className="min-h-screen bg-slate-950 py-8">
      {/* brand header */}
      <div className="mx-auto max-w-4xl px-4">
        <div className="mb-6 flex items-center gap-3">
          {company.logo_path ? (
            <img src={fileUrl(company.logo_path)} alt="logo" className="h-11 w-11 rounded-lg bg-white object-contain p-1" />
          ) : (
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-amber-500 text-slate-950"><HardHat size={22} /></div>
          )}
          <div>
            <div className="font-display text-lg font-bold text-white">{company.name || "TJ Entreprenadkonsult"}</div>
            <div className="text-xs uppercase tracking-widest text-amber-500">Attest av fakturaunderlag</div>
          </div>
        </div>

        <Card className="overflow-hidden bg-white">
          <div className="border-b border-slate-100 bg-slate-50 px-6 py-5">
            <h1 className="font-display text-xl font-extrabold text-slate-900 sm:text-2xl">{s.title}</h1>
            <p className="mt-1 text-sm text-slate-500">
              {p?.name} · Beställare: {p?.contractor_name || "—"} · Period {s.date_from} – {s.date_to}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-0 lg:grid-cols-5">
            {/* entries */}
            <div className="lg:col-span-3 lg:border-r lg:border-slate-100">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900 text-left text-[11px] uppercase tracking-wider text-slate-300">
                    <tr>
                      <th className="px-5 py-3">Datum</th><th className="px-5 py-3">Anställd</th>
                      <th className="px-5 py-3 text-right">Arb.tim</th>
                      {showAmt ? <th className="px-5 py-3 text-right">Belopp</th>
                        : (<><th className="px-5 py-3 text-right">Mask.tim</th><th className="px-5 py-3">Material</th></>)}
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => {
                      const mh = (e.machines || []).reduce((sum, m) => sum + (m.hours || 0), 0);
                      const mats = (e.materials || []).map((m) => `${fmtNum(m.quantity)} ${m.unit} ${m.name}`).join("; ") || "—";
                      return (
                        <tr key={e.id} className="border-b border-slate-100 align-top">
                          <td className="px-5 py-3 font-mono text-slate-500">{e.date}</td>
                          <td className="px-5 py-3 text-slate-800">{e.employee_name}</td>
                          <td className="px-5 py-3 text-right text-slate-600">{fmtNum(e.work_hours)}</td>
                          {showAmt ? <td className="px-5 py-3 text-right font-semibold text-slate-900">{fmt(e.cost.total, cur)}</td>
                            : (<><td className="px-5 py-3 text-right text-slate-600">{fmtNum(mh)}</td><td className="px-5 py-3 text-slate-600">{mats}</td></>)}
                        </tr>
                      );
                    })}
                    {entries.length === 0 && <tr><td colSpan={showAmt ? 4 : 5} className="px-5 py-8 text-center text-slate-400">Inga rapporter.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* totals + sign */}
            <div className="lg:col-span-2">
              <div className="px-6 py-5">
                {showAmt ? (
                  <>
                    <Row label={`Arbete (${fmtNum(summary.work_hours)} h)`} value={fmt(summary.labor, cur)} />
                    <Row label="Maskin" value={fmt(summary.machine, cur)} />
                    <Row label="Material" value={fmt(summary.material, cur)} />
                    <Row label={`Moms (${fmtNum(company.vat_rate)}%)`} value={fmt(vat, cur)} />
                    <Row label="Att betala" value={fmt(total, cur)} strong />
                  </>
                ) : (
                  <>
                    <Row label="Arbetstimmar" value={`${fmtNum(summary.work_hours)} h`} />
                    <Row label="Maskintimmar" value={`${fmtNum(summary.machine_hours)} h`} />
                    {(summary.materials_breakdown || []).map((mb, i) => (
                      <Row key={i} label={`Material · ${mb.name}`} value={`${fmtNum(mb.quantity)} ${mb.unit}`} />
                    ))}
                    {(summary.materials_breakdown || []).length === 0 && <Row label="Material" value="—" />}
                  </>
                )}
              </div>

              <div className="border-t border-slate-100 bg-slate-50 px-6 py-5">
                {signed ? (
                  <div className="text-center">
                    <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-emerald-100 px-4 py-1.5 text-sm font-semibold text-emerald-700">
                      <CheckCircle2 size={16} /> Godkänt
                    </div>
                    <p className="text-sm text-slate-600">Signerat av <b>{s.contractor_name}</b></p>
                    <p className="text-xs text-slate-400">{String(s.signed_at).slice(0, 10)}</p>
                    <a href={`${API_BASE}/public/signoffs/${token}/pdf`} target="_blank" rel="noreferrer" className="mt-4 inline-block">
                      <Button variant="dark" data-testid="download-pdf-button"><ExternalLink size={15} /> Ladda ner PDF</Button>
                    </a>
                  </div>
                ) : (
                  <div>
                    <div className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                      <ShieldCheck size={16} className="text-amber-500" /> Godkänn timmar & kostnad
                    </div>
                    <div className="mb-3">
                      <Label className="text-slate-500">Ditt namn</Label>
                      <input data-testid="contractor-name-input" value={name} onChange={(e) => setName(e.target.value)}
                        placeholder="För- och efternamn"
                        className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none focus:border-amber-500" />
                    </div>
                    <div className="mb-4"><SignaturePad onChange={setSig} /></div>
                    <button data-testid="submit-signoff-button" onClick={submit} disabled={busy}
                      className="w-full rounded-lg bg-amber-500 px-4 py-3 font-display font-bold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-50">
                      {busy ? "Skickar…" : "Godkänn & signera"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        {notes.length > 0 && (
          <Card className="mt-6 overflow-hidden bg-white" data-testid="signoff-notes">
            <div className="border-b border-slate-100 bg-slate-50 px-6 py-4">
              <h2 className="font-display text-base font-bold text-slate-900">Anteckningar ({notes.length})</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {notes.map((n, i) => (
                <div key={i} className="px-6 py-3" data-testid={`signoff-note-${i}`}>
                  <div className="text-[11px] font-medium uppercase tracking-wider text-slate-400">{n.date} · {n.emp}</div>
                  <p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-700">{n.note}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {photos.length > 0 && (
          <Card className="mt-6 overflow-hidden bg-white" data-testid="signoff-photos">
            <div className="border-b border-slate-100 bg-slate-50 px-6 py-4">
              <h2 className="font-display text-base font-bold text-slate-900">Foton från arbetet ({photos.length})</h2>
            </div>
            <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3">
              {photos.map((ph, i) => (
                <a key={i} href={fileUrl(ph.path)} target="_blank" rel="noreferrer" className="group block" data-testid={`signoff-photo-${i}`}>
                  <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                    <img src={fileUrl(ph.path)} alt={`Foto ${ph.date}`} loading="lazy"
                      className="h-32 w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">{ph.date} · {ph.emp}</div>
                </a>
              ))}
            </div>
          </Card>
        )}

        <p className="mt-4 text-center text-xs text-slate-600">
          {company.name} {company.org_number && `· ${company.org_number}`} · Genererat via TJ Entreprenadkonsult
        </p>
      </div>
    </div>
  );
}
